import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import LegalPage from './LegalPage';
import { PAGE_SLUGS, PageSlug, PageUrl, getPageUrls } from '../api/pages';

// Older short addresses that redirect to each page, besides its original slug
// (the API refuses these as another page's path).
const ALIASES: Record<PageSlug, string[]> = {
  'privacy-policy': ['privacy'],
  'terms-of-service': ['terms'],
  'delete-account': [],
};

const FALLBACK_TITLE: Record<PageSlug, string> = {
  'privacy-policy': 'Privacy Policy',
  'terms-of-service': 'Terms of Service',
  'delete-account': 'Delete Your Account',
};

// Used when the API can't be reached: every page at its original address.
const DEFAULT_PAGES: PageUrl[] = PAGE_SLUGS.map((slug) => ({
  slug,
  title: FALLBACK_TITLE[slug],
  path: `/${slug}`,
  externalUrl: null,
  url: '',
  updatedAt: '',
}));

const trimPath = (path: string): string => path.replace(/^\/+|\/+$/g, '').toLowerCase();

/**
 * Catch-all route: shows the Privacy Policy / Terms page at whatever address
 * the admin gave it, sends the original addresses (/privacy-policy, /privacy,
 * …) to the current one, and everything else to the admin login.
 */
const PageRoute: React.FC = () => {
  const location = useLocation();
  const [pages, setPages] = useState<PageUrl[] | null>(null);

  useEffect(() => {
    let alive = true;
    getPageUrls().then(
      (list) => alive && setPages(list),
      () => alive && setPages(DEFAULT_PAGES)
    );
    return () => {
      alive = false;
    };
  }, []);

  if (!pages) return null;

  const here = trimPath(location.pathname);
  const page = pages.find((p) => trimPath(p.path) === here);
  if (page) return <LegalPage slug={page.slug} fallbackTitle={page.title} />;

  const moved = pages.find((p) => p.slug === here || (ALIASES[p.slug] || []).includes(here));
  if (moved) return <Navigate to={{ pathname: moved.path, hash: location.hash }} replace />;

  return <Navigate to="/admin/login" replace />;
};

export default PageRoute;
